import React from "react";
import { FormattedMessage } from "react-intl";
import { ProductTechnicalSheetProps } from "../types";
import {
  formatCurrency,
  formatNumber,
  formatPercentage,
} from "../quotation-draft/lib/format";
import { getPackagesToOrder } from "../quotation-draft/lib/utilities";
import ColorPreviewBox from "../../../ui-components/ColorPreviewBox";
import authenticationService from "../../../services/AuthenticationService";
import {splitCode} from "../../kcolor/utils/utils";
import {DisplayResa} from "../../kcolor/utils/DisplayResa";

export const TechnicalSheet = ({
  productInfo,
  updatedQuantity,
  slim,
  totals,
}: ProductTechnicalSheetProps) => {
  const {
    product: {
      art_des_it,
      art_des_cmpy,
      base_um: um,
      prezzo: price,
      resa,
      unita_mis_resa: um_resa,
      min_qty,
      link_image,
      art_cod,
    },
    quantity,
    isKcc,
    isKcolor,
    kcolor_color_code,
    kcolor_color_rgb,
  } = productInfo;
  const technicalSheet = [
    {
      key: "product_code",
      defaultMessage: "Codice Prodotto",
      um: null,
      value: productInfo.product.art_cod,
    },
    {
      key: "resa",
      um: um_resa?<DisplayResa resa={um_resa} />: null,
      value: resa,
      update: null,
      renderSlim: false,
    },
    {
      key: "color",
      um: "",
      value: kcolor_color_code,
      renderSlim: false,
    },
    ...(!isKcolor || true ? [{
      key: "unit_price",
      um: `€/${um?.toLowerCase()}`,
      value: price,
      update: null,
      renderSlim: false,
    }] : []),
    {
      key: "quantity",
      um: um?.toLowerCase(),
      value: quantity,
      update: updatedQuantity,
      renderSlim: true,
    },
    {
      key: "packages_to_order",
      um: um?.toLowerCase(),
      value: getPackagesToOrder(quantity, min_qty),
      update: getPackagesToOrder(updatedQuantity, min_qty),
      renderSlim: false,
    },
    ...(slim
      ? [
          {
            key: "price",
            um: "€",
            value: totals.original,
            update: null,
            renderSlim: true,
          },
        ]
      : []),
  ];
  const gridCls = `grid grid-cols-3 gap-y-1 sm:gap-y-2 gap-x-2 sm:gap-x-10 items-start sm:items-center text-xs ${
    !slim ? "sm:text-sm" : ""
  }`;
  const totalHasDiscount = totals.original !== totals.discounted;
  const userData = authenticationService.getUserData();
  return (
    <div>
      <div className={gridCls}>
        {technicalSheet
          .filter(({value, um, renderSlim}) => {
            const isValidValue = value !== null && value !== undefined;
            const isValidUm =
              typeof um === 'string' ? um.toLowerCase() !== 'null' : true;
            const shouldRender = !slim || renderSlim;

            return isValidValue && isValidUm && shouldRender;
          })
          .map(({ key, um, value, update, defaultMessage }, idx) => {
            const hasUpdate = !!update || update === 0;
            return (
              <React.Fragment key={idx}>
                <div
                  className={slim ? "col-span-1" : "col-span-3 sm:col-span-1"}
                >
                  <FormattedMessage
                    id={key}
                    {...(defaultMessage ? {defaultMessage} : {})}
                  />
                </div>
                {key !== 'color' ? <div
                  className={`flex gap-2 items-center ${
                    slim
                      ? "col-span-2 mb-0"
                      : "col-span-3 sm:col-span-2 mb-1 sm:mb-0"
                  }`}
                >
                  {hasUpdate && (
                    <div className="font-semibold text-red-600">
                      {key === "packages_to_order"
                        ? update
                        : formatNumber(update as number, {
                            maximumFractionDigits: 2,
                          })}{" "}
                      {um}
                    </div>
                  )}
                  <div
                    className={
                      hasUpdate ? "line-through text-gray-400" : "font-semibold"
                    }
                  >
                    {key === "packages_to_order" || key === "product_code"
                      ? value
                      : formatNumber(value as number, {
                          maximumFractionDigits: 2,
                        })}{" "}
                    {um}
                  </div>
                </div>:
                  <div
                    className={`flex gap-2 items-center ${
                      slim
                        ? "col-span-2 mb-0"
                        : "col-span-3 sm:col-span-2 mb-1 sm:mb-0"
                    }`}>
                  <span className={"font-bold"}>
                    {splitCode(value as string)}
                  </span>
                    <ColorPreviewBox
                      selectedColorRGB={kcolor_color_rgb}
                      className={"w-6 h-6"}
                    />
                  </div>
                }
              </React.Fragment>
            );
          })}
      </div>
      {/* ---------- TOTAL ---------- */}
      {!slim && (
        <div className={gridCls}>
          <div className="font-semibold sm:text-xl mt-5 col-span-3 sm:col-span-1">
            <FormattedMessage id="total" />
          </div>
          <div className="flex gap-3 items-center text-base sm:text-xl sm:mt-5 col-span-3 sm:col-span-2">
            {(price === null || price === undefined) && userData.role_type !== 'resale_tintometer' ?
              <span>
                <FormattedMessage
                  id="price_not_available"
                  defaultMessage="prezzo bloccato"
                />
              </span> :
              isKcolor &&
              userData.role_type === 'resale_tintometer' &&
              (price === null || price === undefined) ?
                <span className={"font-semibold"}>
                  ___.__ €
                </span> :
              <>{totalHasDiscount && (
                <>
                <span className="font-semibold">
                  {formatCurrency(totals.discounted)}
                </span>
                  <span className="font-semibold text-red-600">
                  (
                    {formatPercentage(
                      (totals.discounted - totals.original) / totals.original
                    )}
                    )
                </span>
                </>
              )}
                <span
                  className={
                    totalHasDiscount
                      ? "line-through text-gray-400 text-sm mt-0.5"
                      : "font-semibold"
                  }
                >
              {formatCurrency(totals.original)}
            </span>
              </>
            }
          </div>
        </div>
      )}
    </div>
  );
};
